import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Alert,
  CircularProgress,
  Box,
  Typography,
} from '@mui/material';
import { FabrikUser } from '../../../types';
import { useAuth } from '../../../contexts/AuthContext';
import { updateUserEmail, updateUserProfile } from '../../../services/api/endpoints/users';

interface EditUserProfileDialogProps {
  open: boolean;
  onClose: () => void;
  user: FabrikUser;
  onSuccess: () => void;
}

const EditUserProfileDialog: React.FC<EditUserProfileDialogProps> = ({
  open,
  onClose,
  user,
  onSuccess,
}) => {
  const { user: authUser } = useAuth();
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [emailError, setEmailError] = useState<string | null>(null);

  // Initialize form fields when dialog opens or user changes
  useEffect(() => {
    if (open && user) {
      setFirstName(user.first_name || '');
      setLastName(user.last_name || '');
      setEmail(user.email || '');
      setError(null);
      setEmailError(null);
    }
  }, [open, user]);

  // Email validation
  const validateEmail = (email: string): boolean => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const handleEmailChange = (value: string) => {
    setEmail(value);
    if (value && !validateEmail(value)) {
      setEmailError('Please enter a valid email address');
    } else {
      setEmailError(null);
    }
  };

  const handleSave = async () => {
    if (!authUser?.token) {
      setError('Authentication required');
      return;
    }

    // Validate email if it was changed
    if (email !== user.email && !validateEmail(email)) {
      setEmailError('Please enter a valid email address');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const emailChanged = email !== user.email;
      const nameChanged = 
        firstName !== (user.first_name || '') || 
        lastName !== (user.last_name || '');

      // Update email if changed
      if (emailChanged) {
        await updateUserEmail(authUser.token, user.id, email);
      }

      // Update profile if name changed
      if (nameChanged) {
        await updateUserProfile(authUser.token, user.id, {
          first_name: firstName,
          last_name: lastName,
        });
      }

      // Call success callback
      onSuccess();
      onClose();
    } catch (err: any) {
      console.error('Error updating user profile:', err);
      
      // The API client now includes the backend error message in err.message
      const errorMessage = err?.message || 'Failed to update user profile. Please try again.';
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    onClose();
  };

  const hasChanges = 
    firstName !== (user.first_name || '') ||
    lastName !== (user.last_name || '') ||
    email !== user.email;

  const isValid = !emailError && email.trim() !== '';

  return (
    <Dialog
      open={open}
      onClose={handleCancel}
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle>Edit User Profile</DialogTitle>
      <DialogContent>
        <Box sx={{ pt: 1 }}>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          <TextField
            label="First Name"
            value={firstName}
            onChange={(e) => setFirstName(e.target.value)}
            fullWidth
            margin="normal"
            disabled={loading}
          />

          <TextField
            label="Last Name"
            value={lastName}
            onChange={(e) => setLastName(e.target.value)}
            fullWidth
            margin="normal"
            disabled={loading}
          />

          <TextField
            label="Email"
            value={email}
            onChange={(e) => handleEmailChange(e.target.value)}
            fullWidth
            margin="normal"
            disabled={loading}
            error={!!emailError}
            helperText={emailError}
            type="email"
          />

          {email !== user.email && (
            <Alert severity="warning" sx={{ mt: 2 }}>
              <Typography variant="body2">
                Changing the email address will affect the user's authentication. 
                They may need to sign in again with the new email.
              </Typography>
            </Alert>
          )}
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button 
          onClick={handleCancel} 
          disabled={loading}
        >
          Cancel
        </Button>
        <Button
          onClick={handleSave}
          variant="contained"
          disabled={loading || !hasChanges || !isValid}
          startIcon={loading ? <CircularProgress size={20} /> : null}
        >
          {loading ? 'Saving...' : 'Save Changes'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default EditUserProfileDialog;
